import Dialog, { DialogProps } from '../../core/Dialog';
import DataTable from '../../core/DataTable';
import Text from '../../core/Text';
import View from '../../core/View';

type Props = {
  /** 접수 실패한 수신번호. sendFailedVariables 와 순서가 같다. */
  phoneNumbers: readonly string[];
  /** 대상별 치환 변수값 (번호 제외) */
  variables: readonly Record<string, string>[];
} & DialogProps;

// 같은 번호가 여러 행에 있을 수 있어(대상마다 변수값이 다름) 인덱스를 키로 쓴다.
type FailedRow = { id: number; phoneNumber: string; variables: Record<string, string> };

/**
 * 비즈뿌리오 발송 접수가 확인되지 않은 대상 목록.
 *
 * 접수 응답을 못 받았을 뿐 실제로는 접수됐을 수 있다(타임아웃 등). 그래서 '미발송'이라
 * 단정하지 않고, 콘솔에서 확인한 뒤 미접수 건만 다시 보내도록 안내한다.
 *
 * 재발송은 이 표를 복사해 TCMS 에서 새로 발송을 만드는 방식이라, 여기서는 보여주기만 한다.
 */
const BrandMessageSendFailedDialog = ({ phoneNumbers, variables, ...props }: Props) => {
  const rows: FailedRow[] = phoneNumbers.map((phoneNumber, index) => ({
    id: index,
    phoneNumber,
    variables: variables[index] ?? {},
  }));

  // 변수 칼럼은 대상마다 다를 수 있어 전체에서 모아 순서를 고정한다.
  const variableNames = rows.reduce<string[]>((names, { variables: rowVariables }) => {
    Object.keys(rowVariables).forEach((name) => {
      if (!names.includes(name)) names.push(name);
    });
    return names;
  }, []);

  return (
    <Dialog {...props}>
      <Dialog.Header>발송 접수 미확인 ({rows.length.toLocaleString()}건)</Dialog.Header>
      <Dialog.Body>
        <View sx={{ marginBottom: 3 }}>
          <Text sx={{ fontSize: 1, color: 'text.secondary' }}>
            비즈뿌리오 발송 접수가 확인되지 않은 대상입니다. 접수 응답을 못 받았을 뿐 실제로는 나갔을 수도 있으니,
            콘솔에서 확인한 뒤 미접수 건만 아래 내용으로 다시 보내 주세요.
          </Text>
        </View>
        <DataTable
          rows={rows}
          columns={[
            {
              field: 'phoneNumber',
              title: '수신번호',
              renderValue: ({ phoneNumber }) => <Text>{phoneNumber}</Text>,
              width: 140,
            },
            ...variableNames.map((name) => ({
              field: name,
              title: name,
              renderValue: ({ variables: rowVariables }: FailedRow) => <Text>{rowVariables[name] ?? '-'}</Text>,
              width: 140,
            })),
          ]}
        />
      </Dialog.Body>
    </Dialog>
  );
};

export default BrandMessageSendFailedDialog;
